import { Button, Input, Modal, message, Space, Table } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import {
  type AdminCommerceUser,
  adminCommerceApi,
} from '@/services/adminCommerceApi';

interface UserSearchProps {
  brandId: string;
  scope: 'credits' | 'orders';
  disabled?: boolean;
  busy?: boolean;
  onSearchStart?: () => void;
  onSelect: (userId: string) => void;
}

export function UserSearch({
  brandId,
  scope,
  disabled,
  busy,
  onSearchStart,
  onSelect,
}: UserSearchProps) {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<AdminCommerceUser[]>([]);
  const [nextAfterUserId, setNextAfterUserId] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const requestVersion = useRef(0);

  useEffect(
    () => () => {
      requestVersion.current += 1;
    },
    [],
  );

  const searchUsers = async (keyword: string, afterUserId = '') => {
    if (disabled || busy || !brandId) return;
    const normalized = keyword.trim();
    if (!normalized) {
      message.warning('请输入用户 ID、邮箱或昵称');
      return;
    }
    const version = ++requestVersion.current;
    if (!afterUserId) {
      onSearchStart?.();
      if (/^[1-9]\d*$/.test(normalized)) {
        onSelect(normalized);
        return;
      }
      setQuery(normalized);
      setItems([]);
      setNextAfterUserId('');
      setOpen(true);
    }
    setLoading(true);
    try {
      const result = await adminCommerceApi.searchUsers({
        scope,
        brandId,
        query: normalized,
        afterUserId: afterUserId || undefined,
      });
      if (version !== requestVersion.current) return;
      setItems((previous) =>
        afterUserId
          ? [...previous, ...(result.items ?? [])]
          : (result.items ?? []),
      );
      setNextAfterUserId(result.nextAfterUserId ?? '');
    } catch {
      if (version !== requestVersion.current) return;
      message.error('用户查询失败，请重试');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  return (
    <>
      <Input.Search
        aria-label="用户 ID、邮箱或昵称"
        placeholder="用户 ID / 邮箱 / 昵称"
        style={{ width: 384, maxWidth: '100%' }}
        value={input}
        maxLength={256}
        disabled={disabled || busy || !brandId}
        loading={loading || busy}
        enterButton="查询用户"
        onChange={(event) => setInput(event.target.value)}
        onSearch={(value, event) => {
          event?.preventDefault();
          void searchUsers(value);
        }}
      />
      <Modal
        title="选择用户"
        open={open && !disabled}
        width={900}
        footer={null}
        onCancel={() => {
          requestVersion.current += 1;
          setLoading(false);
          setOpen(false);
        }}
      >
        <p className="mb-3 text-sm text-muted-foreground">
          “{query}” 的匹配结果。同邮箱或同昵称可能对应多个账号，请核对 UID
          后选择。
        </p>
        <Table<AdminCommerceUser>
          rowKey="userId"
          dataSource={items}
          loading={loading}
          pagination={false}
          locale={{ emptyText: '未找到匹配用户' }}
          scroll={{ x: 750 }}
          columns={[
            {
              title: '昵称',
              dataIndex: 'name',
              render: (value) => value || '-',
            },
            {
              title: '邮箱',
              dataIndex: 'email',
              render: (value) => value || '-',
            },
            { title: 'UID', dataIndex: 'userId' },
            {
              title: '注册时间',
              dataIndex: 'createdAt',
              render: (value: string) =>
                value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
            },
            {
              title: '操作',
              key: 'select',
              render: (_, user) => (
                <Button
                  type="link"
                  onClick={() => {
                    requestVersion.current += 1;
                    setLoading(false);
                    setOpen(false);
                    setInput(user.userId);
                    onSelect(user.userId);
                  }}
                >
                  选择
                </Button>
              ),
            },
          ]}
        />
        {nextAfterUserId ? (
          <Space className="mt-3">
            <Button
              loading={loading}
              onClick={() => void searchUsers(query, nextAfterUserId)}
            >
              加载更多
            </Button>
          </Space>
        ) : null}
      </Modal>
    </>
  );
}
